import type { PlayerConfig } from '../config/player.config';
import type { WorldConfig } from '../config/world.config';
import type { EventBus } from '../core/EventBus';
import type { GameEvents } from '../core/GameEvents';
import { InputAction, type GameplayAction } from '../input/InputAction';
import { InputBuffer } from '../input/InputBuffer';
import { laneCenterX } from '../level/segments/segmentBuilders';
import type { TerrainQuery } from '../level/terrain/TerrainQuery';
import type { Aabb } from '../physics/Aabb';
import { deriveJumpPhysics, type JumpPhysics } from '../physics/JumpPhysics';
import { EASINGS, type EasingFunction } from '../utils/easing';
import { clamp } from '../utils/math';
import { StateMachine } from '../utils/StateMachine';
import { AIRBORNE_STATES, PLAYER_STATE_TRANSITIONS, PlayerState } from './PlayerState';
import type { SeatPlacement, VehicleSeat } from './VehicleSeat';

/** Kinematic state in track space (x lateral, y height, distance along the track). */
export interface PlayerSnapshot {
  distance: number;
  x: number;
  y: number;
  colliderHeight: number;
  lateralVelocity: number;
}

export interface PlayerStepContext {
  /** Forward speed for this step (m/s), provided by the difficulty system. */
  readonly speed: number;
  /** Simulation time (s) – used for input buffering. */
  readonly time: number;
  /** Ground height under the player's center – used when no `terrain` is given. */
  readonly groundHeightAt: (distance: number, x: number) => number | null;
  /**
   * Lowest overhead obstacle underside above the given footprint (track space), or null.
   * A slide is held while standing up would hit something.
   */
  readonly ceilingAt?: (minZ: number, maxZ: number, minX: number, maxX: number) => number | null;
  /**
   * Static track geometry (segment surfaces, pits, ceilings). With it the player stands on
   * the highest ground under its footprint, is stopped by walls and ceilings and bumps
   * its head; without it the ground is the plain height field `groundHeightAt`.
   */
  readonly terrain?: TerrainQuery;
  /** Left/right input near a junction: returns true if it was taken as a turn decision. */
  readonly tryTurn?: (direction: 'LEFT' | 'RIGHT') => boolean;
}

/** How the runner met static geometry (the RunDirector decides the consequence). */
export const TerrainContactKind = {
  /** Ran into it – like the front of an obstacle. */
  Front: 'FRONT',
  /** Changed lanes into it – like clipping an obstacle sideways. */
  Side: 'SIDE',
} as const;
export type TerrainContactKind = (typeof TerrainContactKind)[keyof typeof TerrainContactKind];

export const TerrainBlocker = {
  /** A step higher than maxStepHeight (platform edge, ramp side, pit wall). */
  Wall: 'WALL',
  /** An overhead structure lower than the runner (crossbar, pipe roof, pipe shell). */
  Ceiling: 'CEILING',
} as const;
export type TerrainBlocker = (typeof TerrainBlocker)[keyof typeof TerrainBlocker];

export interface TerrainContact {
  readonly kind: TerrainContactKind;
  readonly blocker: TerrainBlocker;
  /** Wall: its top · ceiling: its underside (track-space height). */
  readonly height: number;
}

/** Ids reported in hit/stumble events for terrain contacts (instead of an obstacle id). */
export const TERRAIN_SOURCE_ID: Readonly<Record<TerrainBlocker, string>> = {
  WALL: 'TERRAIN_WALL',
  CEILING: 'TERRAIN_CEILING',
};

const EPSILON = 1e-6;

function snapshot(): PlayerSnapshot {
  return { distance: 0, x: 0, y: 0, colliderHeight: 0, lateralVelocity: 0 };
}

/**
 * Kinematic character controller – deliberately no rigid-body simulation.
 * Runs in fixed steps, knows nothing about rendering. PlayerView reads
 * `previous`/`current` and interpolates.
 *
 * Terrain (when `ctx.terrain` is given), resolved axis by axis each step:
 * - lateral, then forward: the strip the footprint newly enters must not hold ground
 *   higher than a step above the feet (wall) or a hard ceiling below the head. If it
 *   does, the move is undone and a TerrainContact is reported (side: bounce back).
 * - vertical: the runner stands on the highest ground under its footprint, steps up/down
 *   by at most maxStepHeight (more = walking off an edge), bumps its head on ceilings.
 * - walkable tops (pipe crowns, container roofs) count as ground only once the feet are within
 *   a step of them (topReach) – below that they are ceilings / obstacle fronts in the way.
 * - over a pit and more than pitFallDepth below its rim, the runner `hasFallen`.
 *
 * DRIVING (dozer set piece) is a state of its own: the runner sits on a vehicle (VehicleSeat), steers it with the
 * lane logic at the vehicle's pace and jumps off with jump; slide does nothing. Terrain, gravity and the runner's own
 * collider do not apply – the vehicle's body is the collider. Running, jumping and sliding stay untouched.
 */
export class PlayerController {
  readonly previous: PlayerSnapshot = snapshot();
  readonly current: PlayerSnapshot = snapshot();

  private readonly fsm = new StateMachine<PlayerState>(PlayerState.Running, PLAYER_STATE_TRANSITIONS);
  private readonly buffer: InputBuffer;
  private readonly physics: JumpPhysics;
  private readonly ease: EasingFunction;

  private lane = 0;
  private previousLane = 0;
  private laneFromX = 0;
  private laneToX = 0;
  private laneElapsed = 0;
  private laneDuration = 0;

  private vy = 0;
  private grounded = true;
  private coyoteTimer = 0;
  private airJumpsUsed = 0;
  private slideTimer = 0;
  private slideOnLand = false;
  /** Slide input still held (key down / finger on screen): the slide lasts until release. */
  private slideHeld = false;
  /** The current hit happened in the air (presentation: trip instead of crash). */
  private hitAirborne = false;
  private forwardSpeed = 0;

  /** No ground under the footprint in the last step. */
  private overGap = false;
  /** Height of the last ground under the runner – the rim of a pit it is over. */
  private rimHeight = 0;
  /** Height the runner last stood on (road, container roof, the shell's upper floor) – kept through a jump. */
  private level = 0;
  private fallen = false;
  private contact: TerrainContact | null = null;
  /** The vehicle the runner rides (DRIVING), or null. */
  private seat: VehicleSeat | null = null;

  constructor(
    private readonly config: PlayerConfig,
    private readonly world: WorldConfig,
    private readonly events: EventBus<GameEvents>,
  ) {
    this.buffer = new InputBuffer(config.inputBufferTime);
    this.physics = deriveJumpPhysics(config);
    this.ease = EASINGS[config.laneChangeEasing];
    this.fsm.onChange((from, to) => this.events.emit('player:stateChanged', { from, to }));
    this.reset();
  }

  // ── Queries ──────────────────────────────────────────────────────────────

  get state(): PlayerState {
    return this.fsm.state;
  }

  get timeInState(): number {
    return this.fsm.timeInState;
  }

  get currentLane(): number {
    return this.lane;
  }

  get isGrounded(): boolean {
    return this.grounded;
  }

  /**
   * Height the runner stands on, held through a jump: the camera follows it fully and only the jump above it
   * partially. Never above the runner (a fall off a roof is followed down), never below the road (a pit fall stays
   * damped as before).
   */
  get standingLevel(): number {
    return Math.max(this.world.groundHeight, Math.min(this.level, this.current.y));
  }

  get isAirborne(): boolean {
    return AIRBORNE_STATES.includes(this.fsm.state) || !this.grounded;
  }

  get isSliding(): boolean {
    return this.fsm.state === PlayerState.Sliding;
  }

  get isAlive(): boolean {
    return this.fsm.state !== PlayerState.Dead;
  }

  /** HIT or DEAD – no further collisions are processed in this step. */
  get isIncapacitated(): boolean {
    return this.fsm.is(PlayerState.Hit, PlayerState.Dead);
  }

  /** Fell into a pit (below its rim by more than pitFallDepth) – the RunDirector decides: life or game over. */
  get hasFallen(): boolean {
    return this.fallen;
  }

  get verticalVelocity(): number {
    return this.vy;
  }

  get speed(): number {
    return this.forwardSpeed;
  }

  get jumpPhysics(): JumpPhysics {
    return this.physics;
  }

  /** The vehicle the runner rides (DRIVING), or null. */
  get vehicle(): VehicleSeat | null {
    return this.seat;
  }

  /** Track-space collider of the current (or previous) step. */
  colliderOf(which: 'current' | 'previous', out: Aabb): Aabb {
    const s = which === 'current' ? this.current : this.previous;
    if (this.seat) {
      // Driving: the vehicle's body around the seat – it meets the obstacles, heaps and bottles of the lanes.
      const body = this.seat.body;
      out.minX = s.x - body.width / 2;
      out.maxX = s.x + body.width / 2;
      out.minY = s.y;
      out.maxY = s.y + body.height;
      out.minZ = s.distance - body.rear;
      out.maxZ = s.distance + body.front;
      return out;
    }
    const { width, depth } = this.config.collider;
    out.minX = s.x - width / 2;
    out.maxX = s.x + width / 2;
    out.minY = s.y;
    out.maxY = s.y + s.colliderHeight;
    out.minZ = s.distance - depth / 2;
    out.maxZ = s.distance + depth / 2;
    return out;
  }

  /** Contact with walls/ceilings in the last step (cleared by the next step), taken by the RunDirector. */
  takeTerrainContact(): TerrainContact | null {
    const contact = this.contact;
    this.contact = null;
    return contact;
  }

  // ── Commands ─────────────────────────────────────────────────────────────

  reset(distance = 0): void {
    this.fsm.reset(PlayerState.Running);
    this.buffer.clear();
    this.lane = this.previousLane = clamp(this.config.startLane, 0, this.world.laneCount - 1);
    const x = laneCenterX(this.world, this.lane);
    this.laneFromX = this.laneToX = x;
    this.laneElapsed = this.laneDuration = 0;
    this.vy = 0;
    this.grounded = true;
    this.coyoteTimer = 0;
    this.airJumpsUsed = 0;
    this.slideTimer = 0;
    this.slideOnLand = false;
    this.slideHeld = false;
    this.forwardSpeed = 0;
    this.overGap = false;
    this.rimHeight = this.world.groundHeight;
    this.level = this.world.groundHeight;
    this.fallen = false;
    this.contact = null;
    this.seat = null;
    for (const s of [this.previous, this.current]) {
      s.distance = distance;
      s.x = x;
      s.y = this.world.groundHeight;
      s.colliderHeight = this.config.collider.standingHeight;
      s.lateralVelocity = 0;
    }
  }

  enqueue(action: GameplayAction, time: number): void {
    this.buffer.push(action, time);
  }

  /** Forget held inputs (pause): a release during the pause never reaches the buffer. */
  releaseHolds(): void {
    this.slideHeld = false;
  }

  step(dt: number, ctx: PlayerStepContext): void {
    Object.assign(this.previous, this.current);
    this.contact = null;
    this.fsm.update(dt);
    if (this.fsm.state === PlayerState.Dead) {
      this.settleDead(dt, ctx);
      return;
    }
    if (this.seat) {
      this.stepDriving(dt, ctx, this.seat);
      return;
    }

    if (this.fsm.state !== PlayerState.Hit) {
      this.buffer.process(ctx.time, (action) => this.execute(action, ctx));
    } else {
      this.buffer.clear();
    }

    this.updateLateral(dt, ctx);
    // Advance first, then resolve ground at the new position (no step of unsupported travel past a ledge).
    this.forwardSpeed = this.fsm.state === PlayerState.Hit ? 0 : ctx.speed;
    this.advance(dt, ctx);
    this.updateVertical(dt, ctx);
    this.updateTimedStates(dt, ctx);
    this.current.colliderHeight = this.bodyHeight();
  }

  /**
   * Re-anchors the player on a new path after a 90° turn (TurnController).
   * Taken at the corner, the lateral offset is unchanged: lane and a lane change in
   * progress carry over as they are. A late turn shifts the lateral offset (the world
   * position stays): the player settles into the nearest lane (turnSettleDuration) –
   * a lane change keeps its target, shifted the same way.
   */
  applyTurn(distance: number, lateral: number): void {
    const shift = lateral - this.current.x;
    for (const s of [this.previous, this.current]) {
      s.distance = distance;
      s.x = lateral;
      s.lateralVelocity = 0;
    }
    if (Math.abs(shift) < EPSILON) return;
    const lane = this.nearestLane(this.laneToX + shift);
    this.previousLane = lane;
    this.beginLaneChange(lane, this.config.turnSettleDuration);
  }

  /**
   * Pushed one lane sideways by something else (the ructec-Bulli's rival shoves the van) – no input, no lane-change
   * event. False at the edge of the road (nowhere to go).
   */
  shoveLane(direction: -1 | 1): boolean {
    const target = this.lane + direction;
    if (target < 0 || target >= this.world.laneCount) return false;
    this.previousLane = this.lane;
    this.beginLaneChange(target);
    return true;
  }

  /** Side collision: return to the lane the player came from. */
  bounceBack(): void {
    if (this.previousLane === this.lane) return;
    this.beginLaneChange(this.previousLane);
    this.previousLane = this.lane;
  }

  /** Minor mistake – short stumble animation, run continues. */
  stumble(): void {
    if (this.grounded && this.fsm.canTransition(PlayerState.Stumbling)) {
      this.slideTimer = 0;
      this.slideHeld = false;
      this.fsm.transition(PlayerState.Stumbling);
    }
  }

  /** True when the current hit happened in the air (a jump onto something did not make it) – presentation only. */
  get hitWhileAirborne(): boolean {
    return this.hitAirborne;
  }

  /** Frontal collision – player stops briefly (life lost or death decided by RunDirector). */
  hit(): void {
    if (!this.fsm.canTransition(PlayerState.Hit)) return;
    // Driving: the machine stops dead – the rider is thrown over the blade and trips there (then the usual hit).
    this.throwOff();
    this.hitAirborne = !this.grounded;
    this.slideTimer = 0;
    this.slideOnLand = false;
    this.slideHeld = false;
    this.current.distance -= this.config.hitPushBack;
    this.previous.distance = this.current.distance;
    this.fsm.transition(PlayerState.Hit);
  }

  /** Continue running after a hit that cost a life. */
  recover(): void {
    if (this.fsm.state !== PlayerState.Hit) return;
    this.fsm.transition(PlayerState.Running);
    if (!this.grounded) this.fsm.transition(PlayerState.Falling);
  }

  kill(): void {
    if (this.fsm.state === PlayerState.Dead) return;
    this.throwOff(); // never left lying inside a vehicle
    this.fsm.transition(PlayerState.Dead);
  }

  /** Puts the runner down on safe ground (after a fall into a pit), standing in the nearest lane. */
  respawn(distance: number, x: number, y: number): void {
    this.seat = null;
    const lane = this.nearestLane(x);
    this.lane = this.previousLane = lane;
    this.laneFromX = this.laneToX = x;
    this.laneElapsed = this.laneDuration = 0;
    for (const s of [this.previous, this.current]) {
      s.distance = distance;
      s.x = x;
      s.y = y;
      s.lateralVelocity = 0;
    }
    this.vy = 0;
    this.grounded = true;
    this.coyoteTimer = 0;
    this.airJumpsUsed = 0;
    this.slideOnLand = false;
    this.overGap = false;
    this.rimHeight = y;
    this.fallen = false;
    if (this.fsm.canTransition(PlayerState.Landing)) this.fsm.transition(PlayerState.Landing);
  }

  /**
   * An invulnerable runner meets static geometry: instead of crashing it hops onto a
   * wall (vault) or ducks under a ceiling – the counterpart of smashing through obstacles.
   */
  passTerrain(contact: TerrainContact): void {
    if (contact.kind !== TerrainContactKind.Front || this.isIncapacitated) return;
    if (contact.blocker === TerrainBlocker.Wall) {
      if (!this.grounded || !this.fsm.canTransition(PlayerState.Jumping)) return; // pressed against it until landed
      const rise = Math.max(0, contact.height - this.current.y) + this.config.vaultClearance;
      this.vy = Math.sqrt(2 * this.physics.gravity * rise);
      this.grounded = false;
      this.coyoteTimer = 0;
      this.slideTimer = 0;
      this.slideOnLand = false;
      this.slideHeld = false;
      this.fsm.transition(PlayerState.Jumping);
      return;
    }
    if (contact.height - this.current.y < this.config.collider.slidingHeight) return; // no room to duck
    if (this.grounded) {
      if (this.fsm.canTransition(PlayerState.Sliding)) this.startSlide();
    } else {
      this.vy = Math.min(this.vy, -this.config.fastFallSpeed);
      this.slideOnLand = true;
    }
  }

  /**
   * Takes the seat of a vehicle (DozerRide, from the air): the runner sits on its centre line, in its lane, on its
   * ground. Buffered inputs are dropped – a jump pressed on the way up must not throw the rider straight off again.
   */
  mount(seat: VehicleSeat, at: SeatPlacement): void {
    if (this.seat || !this.fsm.canTransition(PlayerState.Driving)) return;
    this.seat = seat;
    this.buffer.clear();
    this.lane = this.previousLane = clamp(at.lane, 0, this.world.laneCount - 1);
    const x = laneCenterX(this.world, this.lane);
    this.laneFromX = this.laneToX = x;
    this.laneElapsed = this.laneDuration = 0;
    this.vy = 0;
    this.grounded = true;
    this.coyoteTimer = 0;
    this.airJumpsUsed = 0;
    this.slideTimer = 0;
    this.slideOnLand = false;
    this.slideHeld = false;
    this.overGap = false;
    this.rimHeight = at.ground;
    // A cut: the view blends the body onto the seat (PlayerView), the simulation does not interpolate it.
    for (const s of [this.previous, this.current]) {
      s.distance = at.distance;
      s.x = x;
      s.y = at.ground;
      s.lateralVelocity = 0;
      s.colliderHeight = seat.body.height;
    }
    this.fsm.transition(PlayerState.Driving);
  }

  /**
   * Leaves the seat with a jump forward (jump key, end of the ride, catapult over a pit): from the seat height at
   * `launchSpeed` (default: a ground jump) – an ordinary jump from there on, double jump included.
   */
  dismount(launchSpeed = this.physics.jumpVelocity): void {
    const seat = this.seat;
    if (!seat || !this.fsm.canTransition(PlayerState.Jumping)) return;
    this.leaveSeat(seat, 0);
    this.vy = launchSpeed;
    this.fsm.transition(PlayerState.Jumping);
    this.events.emit('player:jumped', { double: false });
  }

  // ── Internals ────────────────────────────────────────────────────────────

  /** Off the seat into the air, `ahead` m in front of it: the runner's own body, rules and pace from here on. */
  private leaveSeat(seat: VehicleSeat, ahead: number): void {
    this.seat = null;
    this.current.distance += ahead;
    this.current.y += seat.seatHeight;
    this.current.colliderHeight = this.bodyHeight();
    // A cut, no interpolation from the vehicle's frame – the view blends the body (PlayerView).
    Object.assign(this.previous, this.current);
    this.grounded = false;
    this.coyoteTimer = 0;
    this.airJumpsUsed = 0;
  }

  /** A hit (or death) while driving: the rider flies over the blade and comes down past what stopped the machine. */
  private throwOff(): void {
    const seat = this.seat;
    if (!seat) return;
    this.leaveSeat(seat, seat.throwOff.ahead);
    this.vy = seat.throwOff.launchSpeed;
  }

  /**
   * DRIVING: steer with the lane logic at the vehicle's pace, jump = jump off, slide = nothing. The vehicle keeps
   * to its ground and goes at the run speed × its factor; its body is the collider (colliderOf).
   */
  private stepDriving(dt: number, ctx: PlayerStepContext, seat: VehicleSeat): void {
    this.buffer.process(ctx.time, (action) => this.executeDriving(action, ctx));
    if (this.seat !== seat) return; // jumped off – the next step runs on foot
    this.updateLateral(dt, ctx);
    this.forwardSpeed = ctx.speed * seat.speedFactor;
    this.current.distance += this.forwardSpeed * dt;
    this.current.colliderHeight = seat.body.height;
  }

  /**
   * Input on the seat. Every action is consumed – slide has no function there. Left/right near a junction turn there
   * (the ructec-Bulli drives through crossings) – the same rule as on foot.
   */
  private executeDriving(action: GameplayAction, ctx: PlayerStepContext): boolean {
    switch (action) {
      case InputAction.MoveLeft:
      case InputAction.MoveRight:
        if (ctx.tryTurn?.(action === InputAction.MoveLeft ? 'LEFT' : 'RIGHT')) return true;
        this.changeLane(action === InputAction.MoveLeft ? -1 : 1);
        return true;
      case InputAction.Jump:
        if (this.seat?.jumpExits !== false) this.dismount();
        return true;
      case InputAction.Slide:
      case InputAction.SlideRelease:
        return true;
    }
  }

  /** One lane to the side (clamped to the track) – on foot and on a vehicle alike. */
  private changeLane(direction: -1 | 1): void {
    const target = clamp(this.lane + direction, 0, this.world.laneCount - 1);
    if (target === this.lane) return;
    this.previousLane = this.lane;
    this.beginLaneChange(target);
    this.events.emit('player:laneChanged', { from: this.previousLane, to: target });
  }

  /** Returns true if the action was consumed. */
  private execute(action: GameplayAction, ctx: PlayerStepContext): boolean {
    switch (action) {
      case InputAction.MoveLeft:
      case InputAction.MoveRight: {
        if (ctx.tryTurn?.(action === InputAction.MoveLeft ? 'LEFT' : 'RIGHT')) return true;
        this.changeLane(action === InputAction.MoveLeft ? -1 : 1);
        return true;
      }
      case InputAction.Jump:
        if (this.grounded || this.coyoteTimer > 0) {
          // No standing up under a crossbar: the jump stays buffered until there is room.
          if (this.isHeadBlocked(ctx)) return false;
          this.groundJump();
          return true;
        }
        if (this.airJumpsUsed < this.config.maxAirJumps) {
          this.doubleJump();
          return true;
        }
        return false;
      case InputAction.Slide:
        this.slideHeld = true;
        if (this.grounded) {
          this.startSlide();
        } else {
          this.vy = Math.min(this.vy, -this.config.fastFallSpeed);
          this.slideOnLand = true;
        }
        return true;
      case InputAction.SlideRelease:
        // A quick swipe/tap still slides the full slideDuration; holding extends it.
        this.slideHeld = false;
        return true;
    }
  }

  private nearestLane(x: number): number {
    return clamp(Math.round(x / this.world.laneWidth + (this.world.laneCount - 1) / 2), 0, this.world.laneCount - 1);
  }

  private bodyHeight(): number {
    return this.isSliding ? this.config.collider.slidingHeight : this.config.collider.standingHeight;
  }

  /** `duration`: fixed time for the move (settling after a turn) instead of the lane-change pace. */
  private beginLaneChange(target: number, duration?: number): void {
    this.lane = target;
    this.laneFromX = this.current.x;
    this.laneToX = laneCenterX(this.world, target);
    // Duration scales with the remaining distance – two quick inputs take two lane-change times
    // (the sequence validator relies on this).
    const fraction = Math.abs(this.laneToX - this.laneFromX) / this.world.laneWidth;
    const perLane = this.seat?.laneChangeDuration ?? this.config.laneChangeDuration;
    this.laneDuration = duration ?? perLane * Math.max(fraction, this.config.laneChangeMinDurationFactor);
    this.laneElapsed = 0;
  }

  private groundJump(): void {
    this.vy = this.physics.jumpVelocity;
    this.grounded = false;
    this.coyoteTimer = 0;
    this.airJumpsUsed = 0;
    this.slideTimer = 0;
    this.slideOnLand = false;
    this.slideHeld = false; // jumping out of a held slide – slide again with a new input
    this.fsm.transition(PlayerState.Jumping);
    this.events.emit('player:jumped', { double: false });
  }

  private doubleJump(): void {
    this.vy = this.physics.doubleJumpVelocity;
    this.airJumpsUsed++;
    this.slideOnLand = false;
    this.fsm.transition(PlayerState.DoubleJumping);
    this.events.emit('player:jumped', { double: true });
  }

  private startSlide(): void {
    this.slideTimer = this.config.slideDuration;
    if (this.fsm.state !== PlayerState.Sliding) {
      this.fsm.transition(PlayerState.Sliding);
      this.events.emit('player:slideStarted', null);
    }
  }

  private updateLateral(dt: number, ctx: PlayerStepContext): void {
    const fromX = this.current.x;
    if (this.laneDuration > 0) {
      this.laneElapsed += dt;
      const t = Math.min(1, this.laneElapsed / this.laneDuration);
      this.current.x = this.laneFromX + (this.laneToX - this.laneFromX) * this.ease(t);
      if (t >= 1) {
        this.current.x = this.laneToX;
        this.laneDuration = 0;
      }
    }
    // A vehicle's body meets obstacles through the collision system – terrain walls are the runner's.
    if (ctx.terrain && !this.seat && this.current.x !== fromX) {
      const half = this.config.collider.width / 2;
      const halfDepth = this.config.collider.depth / 2;
      const right = this.current.x > fromX;
      const blocker = this.blockerIn(
        ctx.terrain,
        this.current.distance - halfDepth,
        this.current.distance + halfDepth,
        right ? fromX + half : this.current.x - half,
        right ? this.current.x + half : fromX - half,
      );
      if (blocker) {
        this.current.x = fromX;
        this.retreatSideways();
        this.report(TerrainContactKind.Side, blocker);
      }
    }
    this.current.lateralVelocity = (this.current.x - this.previous.x) / dt;
  }

  /** Blocked during a lane change: back to the lane the player came from (or stop where it is). */
  private retreatSideways(): void {
    if (this.previousLane !== this.lane) {
      this.bounceBack();
      return;
    }
    this.lane = this.nearestLane(this.current.x);
    this.laneFromX = this.laneToX = this.current.x;
    this.laneDuration = 0;
  }

  private advance(dt: number, ctx: PlayerStepContext): void {
    const from = this.current.distance;
    this.current.distance += this.forwardSpeed * dt;
    if (!ctx.terrain || this.forwardSpeed <= 0) return;
    const half = this.config.collider.width / 2;
    const halfDepth = this.config.collider.depth / 2;
    const s = this.current;
    const blocker = this.blockerIn(ctx.terrain, from + halfDepth, s.distance + halfDepth, s.x - half, s.x + half);
    if (!blocker) return;
    s.distance = from;
    this.report(TerrainContactKind.Front, blocker);
  }

  /**
   * Highest walkable top (pipe crown, container roof) the runner can get onto from where it is:
   * feet + one step. Tops up to it are ground, higher ones are in its way (terrainQueries).
   */
  private get topReach(): number {
    return this.current.y + this.config.maxStepHeight;
  }

  /** Wall (ground higher than a step above the feet) or hard ceiling (below the head) inside the area. */
  private blockerIn(terrain: TerrainQuery, minZ: number, maxZ: number, minX: number, maxX: number): Omit<TerrainContact, 'kind'> | null {
    const y = this.current.y;
    const reach = this.topReach;
    const top = terrain.highestGroundIn(minZ, maxZ, minX, maxX, reach);
    if (top !== null && top > y + this.config.maxStepHeight + EPSILON) return { blocker: TerrainBlocker.Wall, height: top };
    const ceiling = terrain.lowestCeilingIn(minZ, maxZ, minX, maxX, -Infinity, 'hard', reach);
    if (ceiling !== null && ceiling < y + this.bodyHeight() - EPSILON) return { blocker: TerrainBlocker.Ceiling, height: ceiling };
    return null;
  }

  private report(kind: TerrainContactKind, blocker: Omit<TerrainContact, 'kind'>): void {
    // Inside a pit the walls only stop the runner – the fall decides.
    if (this.isInPit()) return;
    if (!this.contact || kind === TerrainContactKind.Front) this.contact = { kind, ...blocker };
  }

  private isInPit(): boolean {
    return !this.grounded && this.overGap && this.current.y < this.rimHeight - this.config.maxStepHeight;
  }

  /** Ground the runner stands on: highest ground under the footprint (terrain) or under the center. */
  private supportAt(ctx: PlayerStepContext): number | null {
    const s = this.current;
    if (!ctx.terrain) return ctx.groundHeightAt(s.distance, s.x);
    const half = this.config.collider.width / 2;
    const halfDepth = this.config.collider.depth / 2;
    return ctx.terrain.highestGroundIn(s.distance - halfDepth, s.distance + halfDepth, s.x - half, s.x + half, this.topReach);
  }

  private updateVertical(dt: number, ctx: PlayerStepContext): void {
    const support = this.supportAt(ctx);
    this.overGap = support === null;
    if (support !== null) this.rimHeight = support;

    if (this.grounded) {
      if (support === null || support < this.current.y - this.config.maxStepHeight - EPSILON) {
        // Walked off an edge (pit or drop): coyote time still allows a ground jump.
        this.grounded = false;
        this.coyoteTimer = this.config.coyoteTime;
        this.vy = 0;
        if (this.fsm.canTransition(PlayerState.Falling)) this.fsm.transition(PlayerState.Falling);
      } else {
        this.current.y = support; // flat ground, ramps and small steps
      }
    } else {
      this.coyoteTimer = Math.max(0, this.coyoteTimer - dt);
      this.vy = Math.max(this.vy - this.physics.gravity * dt, -this.config.maxFallSpeed);
      this.current.y += this.vy * dt;

      if (this.vy <= 0 && this.fsm.is(PlayerState.Jumping, PlayerState.DoubleJumping)) {
        this.fsm.transition(PlayerState.Falling);
      }
      // Never snap up more than a step (+ this step's fall) – higher ground was a wall and stopped us.
      const reach = this.config.maxStepHeight + Math.abs(this.vy) * dt + EPSILON;
      if (support !== null && this.current.y <= support && support - this.current.y <= reach) {
        if (this.vy <= 0) this.land(support);
        else this.current.y = support; // rising past a small step: stay on top of it
      }
    }

    if (ctx.terrain) this.bumpHead(ctx.terrain, support);
    if (this.grounded) this.level = this.current.y;
    if (this.overGap && !this.grounded && this.current.y < this.rimHeight - this.config.pitFallDepth) this.fallen = true;
  }

  /** Ceilings stop upward motion (head bump) and push a runner under a soft deck back down. */
  private bumpHead(terrain: TerrainQuery, support: number | null): void {
    const s = this.current;
    const half = this.config.collider.width / 2;
    const halfDepth = this.config.collider.depth / 2;
    const height = this.bodyHeight();
    const ceiling = terrain.lowestCeilingIn(s.distance - halfDepth, s.distance + halfDepth, s.x - half, s.x + half, s.y, 'all', this.topReach);
    if (ceiling === null || s.y + height <= ceiling + EPSILON) return;

    if (this.grounded) {
      // Standing up under something low (after a respawn or a turn): duck instead.
      if (!this.isSliding && this.fsm.canTransition(PlayerState.Sliding)) this.startSlide();
      return;
    }
    s.y = ceiling - height;
    if (this.vy > 0) {
      this.vy = 0;
      this.events.emit('player:headBumped', null);
    }
    if (support !== null && s.y <= support) this.land(support);
  }

  private land(ground: number): void {
    const impactSpeed = -this.vy;
    this.current.y = ground;
    this.vy = 0;
    this.grounded = true;
    this.coyoteTimer = 0;
    this.airJumpsUsed = 0;
    if (this.fsm.is(PlayerState.Hit, PlayerState.Dead)) return;

    this.events.emit('player:landed', { impactSpeed });
    if (this.slideOnLand) {
      this.slideOnLand = false;
      this.startSlide();
    } else if (this.fsm.canTransition(PlayerState.Landing)) {
      this.fsm.transition(PlayerState.Landing);
    }
  }

  /** A dead runner in mid-air (e.g. in a pit) keeps falling until it lies on the ground or the pit floor. */
  private settleDead(dt: number, ctx: PlayerStepContext): void {
    if (this.grounded) return;
    const before = this.current.y;
    this.vy = Math.max(this.vy - this.physics.gravity * dt, -this.config.maxFallSpeed);
    this.current.y += this.vy * dt;
    const support = this.supportAt(ctx);
    const floor =
      support !== null && support <= before + EPSILON
        ? support
        : (ctx.terrain?.pitFloorAt(this.current.distance, this.current.x) ?? this.world.killHeight);
    if (this.current.y <= floor) {
      this.current.y = floor;
      this.vy = 0;
      this.grounded = true;
    }
  }

  /** True if standing up now would hit an overhead obstacle or a terrain ceiling. */
  private isHeadBlocked(ctx: PlayerStepContext): boolean {
    const { width, depth, standingHeight } = this.config.collider;
    const s = this.current;
    const minZ = s.distance - depth / 2;
    const maxZ = s.distance + depth / 2;
    const limit = s.y + standingHeight;
    const obstacle = ctx.ceilingAt?.(minZ, maxZ, s.x - width / 2, s.x + width / 2) ?? null;
    if (obstacle !== null && obstacle < limit) return true;
    const terrain = ctx.terrain?.lowestCeilingIn(minZ, maxZ, s.x - width / 2, s.x + width / 2, s.y, 'all', this.topReach) ?? null;
    return terrain !== null && terrain < limit - EPSILON;
  }

  private updateTimedStates(dt: number, ctx: PlayerStepContext): void {
    switch (this.fsm.state) {
      case PlayerState.Sliding:
        this.slideTimer -= dt;
        if (this.slideTimer <= 0 && !this.slideHeld && !this.isHeadBlocked(ctx)) this.fsm.transition(PlayerState.Running);
        break;
      case PlayerState.Landing:
        if (this.fsm.timeInState >= this.config.landingDuration) this.fsm.transition(PlayerState.Running);
        break;
      case PlayerState.Stumbling:
        if (this.fsm.timeInState >= this.config.stumbleDuration) this.fsm.transition(PlayerState.Running);
        break;
      default:
        break;
    }
  }
}
